import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LineChartOutlined,
  PictureOutlined,
  PlusOutlined,
  RobotOutlined,
  SaveOutlined,
  ShopOutlined,
  SlidersOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  Alert,
  App,
  Avatar,
  Button,
  Checkbox,
  Col,
  ColorPicker,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Popconfirm,
  Progress,
  Skeleton,
  Space,
  Statistic,
  Switch,
  Tag,
  Typography,
  Upload,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import type { components } from "@/api/types";
import { useRobot } from "@/entities/robot";
import type { RobotCreateBody, ShowcaseBlock } from "@/features/manage-robot";
import {
  SHOWCASE_BLOCKS,
  blocksToBody,
  blocksToForm,
  useCreateRobot,
  useUpdateRobot,
  useResetOffer,
  useUploadRobotImage,
} from "@/features/manage-robot";
import type { PreviewRobot } from "@/features/robot-preview";
import { RobotCardPreview, YieldPreviewCard } from "@/features/robot-preview";
import { errorMessage } from "@/shared/api";
import { ROUTES, SECTIONS } from "@/shared/config";
import { PageHeader, SectionCard } from "@/shared/ui";

type AssetItem = components["schemas"]["AssetItem"];
type OfferBadge = components["schemas"]["OfferBadge"];

/**
 * Форма робота с живым превью (AD-02, ТЗ §23–§32).
 *
 * Значения полей генератора читаются через `Form.useWatch` и уходят в превью
 * строками — ровно в том виде, в каком они пойдут на бэкенд. Приводить их к
 * `number` ради расчёта нельзя: тогда превью считалось бы по одним числам, а
 * начисления шли бы по другим, и смысл AD-02 пропал бы.
 *
 * Полей стало сорок с лишним, и они разложены по карточкам, а не по
 * подзаголовкам внутри одной. Причина не в длине: §25 требует, чтобы
 * автоматические данные читались как «сюда нельзя вводить» с расстояния, а
 * `Typography.Title` не даёт ни рамки, ни места под пометку «только чтение».
 * Заодно заголовки карточек работают якорями на странице в две с половиной
 * тысячи пикселей.
 *
 * `visible_blocks` живёт в форме списком включённых кодов, а не словарём:
 * отсутствующий ключ означает «показывать», и словарь переключателей неизбежно
 * записал бы шесть явных `true` в тело каждого сохранения.
 */
type Values = Omit<
  RobotCreateBody,
  "visible_blocks" | "offer_starts_at" | "offer_ends_at" | "launched_at"
> & {
  /**
   * Дата запуска стратегии (доработки §4.1). Тот же маппинг `Dayjs ↔ ISO`, что
   * у окна кампании ниже, и по той же причине.
   */
  launched_at_ui: Dayjs | null;
  visible_blocks_ui: ShowcaseBlock[];
  /**
   * Окно кампании. В форме это объекты `Dayjs` (их требует `DatePicker`), в
   * теле запроса — ISO-строки. Второй маппинг рядом с `visible_blocks_ui` и по
   * той же причине: форма и контракт описывают одно значение разными типами, и
   * притворяться, что типы совпадают, значит однажды отправить объект.
   *
   * Часовой пояс зафиксирован на `OFFER_TZ`, а не взят из браузера. Оператор
   * думает в московском времени, и с ноутбуком на UTC он поставил бы дедлайн
   * на три часа мимо — а обнаружилось бы это ровно в момент дедлайна.
   */
  offer_starts_at_ui: Dayjs | null;
  offer_ends_at_ui: Dayjs | null;
};

/**
 * Набор сигналов ТЗ §13. Подписи английские — так они стоят и в мини-аппе, и
 * так их называет рынок; переводить «Funding Rate» в «ставка финансирования»
 * значило бы разойтись с витриной.
 *
 * Три последних доступны только фьючерсным роботам: у спотовой пары нет ни
 * открытого интереса, ни ставки финансирования, ни ликвидаций. Запрет держит
 * бэкенд — здесь об этом говорит подсказка под полем.
 */
const AI_SIGNAL_OPTIONS = [
  { value: "price_action", label: "Price Action" },
  { value: "volume", label: "Volume" },
  { value: "momentum", label: "Momentum" },
  { value: "volatility", label: "Volatility" },
  { value: "market_structure", label: "Market Structure" },
  { value: "correlation", label: "Correlation" },
  { value: "btc_dominance", label: "BTC Dominance" },
  { value: "market_sentiment", label: "Market Sentiment" },
  { value: "open_interest", label: "Open Interest (futures)" },
  { value: "funding_rate", label: "Funding Rate (futures)" },
  { value: "liquidations", label: "Liquidations (futures)" },
];

/** Сигналы, которых у спотового робота быть не может (тот же список на бэке). */
const FUTURES_ONLY = new Set(["open_interest", "funding_rate", "liquidations"]);

const MAX_ASSETS = 8;

/** Ярлыки предложения. Коды те же, что в `enums.OfferBadge` на бэкенде. */
const OFFER_BADGES = ["limited", "special", "boosted"] as const;

/**
 * ISO-строка контракта → значение `DatePicker`.
 *
 * Зона — браузерная, и это осознанно: заставлять её быть московской значило бы
 * врать оператору, который сидит не в Москве. Вместо этого форма показывает под
 * полем абсолютный момент вместе со смещением — так ошибка «поставил дедлайн на
 * три часа мимо» видна сразу, а не в момент дедлайна.
 */
function toOfferTime(value: string | null | undefined): Dayjs | null {
  return value ? dayjs(value) : null;
}

const DEFAULTS: Values = {
  code: "",
  name: "",
  description: "",
  how_it_works: "",
  market: "spot",
  risk_level: "medium",
  status: "active",
  sort_order: 0,
  min_amount: "100",
  recommended_amount: null,
  min_topup_amount: "0",
  topup_enabled: true,
  demo_enabled: true,
  declared_yield_12m: "0",
  declared_max_dd: "0",
  // Прогнозная по умолчанию: витринный ряд генерируется из настроек робота, и
  // назвать его фактической историей оператор обязан осознанно.
  yield_type: "target",
  launched_at_ui: null,
  risk_note: null,
  daily_min_pct: "0",
  daily_max_pct: "0",
  positive_chance: "60",
  // Новый робот заводится без комиссии: ставку оператор выставляет осознанно,
  // а не получает по умолчанию.
  profit_fee_pct: "0",
  users_count: 0,
  profit_total: "0",
  assets: [],
  ai_signals: [],
  tier_includes: [],
  exchanges: [],
  pairs: [],
  accent_color: null,
  tagline: null,
  ladder_note: null,
  about_strategy: null,
  disclaimer: null,
  cta_text: null,
  leverage: null,
  timeframe: null,
  trades_per_day: null,
  avg_hold: null,
  trades_per_day_min: null,
  trades_per_day_max: null,
  visible_blocks_ui: [...SHOWCASE_BLOCKS],
  // Кампания выключена: новый робот заводится обычным, а лимитированным
  // становится осознанным переключателем.
  is_limited: false,
  pool_limit: null,
  max_per_user: null,
  offer_badge: "limited",
  offer_priority: 0,
  offer_paused: false,
  offer_starts_at_ui: null,
  offer_ends_at_ui: null,
};

export function RobotFormPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const isNew = params.id === undefined;
  const robotId = isNew ? 0 : Number(params.id);
  const existing = useRobot(robotId, !isNew);

  const [form] = Form.useForm<Values>();
  const create = useCreateRobot();
  const update = useUpdateRobot(robotId);
  const upload = useUploadRobotImage();
  const reset = useResetOffer(robotId);

  // Файл, выбранный до того, как у робота появился id. У ручки загрузки id в
  // пути, поэтому у нового робота файл ждёт здесь и уходит сразу после
  // создания. `preview` — objectURL, чтобы оператор видел, что он выбрал.
  const [pendingImage, setPendingImage] = useState<{ file: File; preview: string } | null>(null);

  // База превью — не поле робота: фиксированной цены у него больше нет, а
  // минимум это порог входа, а не типичный капитал. Поэтому отдельным
  // состоянием, а не `Form.Item`: роботу оно не сохраняется.
  const [previewBase, setPreviewBase] = useState<string | null>(null);

  // Строками, а не числами: InputNumber с `stringMode` отдаёт строку, и именно
  // она уходит и в превью, и на бэкенд.
  const dailyMin = Form.useWatch("daily_min_pct", form);
  const dailyMax = Form.useWatch("daily_max_pct", form);
  const chance = Form.useWatch("positive_chance", form);
  const declared = Form.useWatch("declared_yield_12m", form);
  // Ставка комиссии тоже наблюдается: превью показывает чистую доходность, и
  // без неё оператор сверял бы карточку с цифрой, которой не будет.
  const fee = Form.useWatch("profit_fee_pct", form);
  const minAmount = Form.useWatch("min_amount", form);
  const recommended = Form.useWatch("recommended_amount", form);
  const market = Form.useWatch("market", form);

  // Для превью карточки: те же поля, что рисует список мини-аппа.
  const watchedName = Form.useWatch("name", form);
  const watchedCode = Form.useWatch("code", form);
  const watchedRisk = Form.useWatch("risk_level", form);
  const watchedAssets = Form.useWatch("assets", form);
  const watchedSignals = Form.useWatch("ai_signals", form);
  const watchedUsers = Form.useWatch("users_count", form);
  const watchedTagline = Form.useWatch("tagline", form);
  const watchedCta = Form.useWatch("cta_text", form);
  const watchedAccent = Form.useWatch("accent_color", form);

  // Кампания. `isLimited` гасит поля карточки, `endsAtUi` — показывает
  // абсолютный момент со смещением под полем дедлайна.
  const isLimited = Form.useWatch("is_limited", form) ?? false;
  const endsAtUi = Form.useWatch("offer_ends_at_ui", form) as Dayjs | null | undefined;

  /**
   * Перезапустить кампанию значениями, которые СЕЙЧАС стоят в форме.
   *
   * Не отдельным диалогом с собственными полями: оператор уже ввёл новый пул и
   * новое окно в карточку выше, и второй набор тех же полей означал бы вопрос
   * «а какие из них применятся». Кнопка под шкалой заполненности — там же, где
   * видно, что именно обнуляется.
   */
  const resetOffer = () => {
    const endsAt = form.getFieldValue("offer_ends_at_ui") as Dayjs | null;
    const poolLimit = form.getFieldValue("pool_limit") as string | null;
    if (!endsAt || !poolLimit) {
      void message.error(t("robots.offerResetNeedsWindow"));
      return;
    }
    const startsAt = form.getFieldValue("offer_starts_at_ui") as Dayjs | null;
    const maxPerUser = form.getFieldValue("max_per_user") as string | null;
    reset.mutate(
      {
        pool_limit: poolLimit,
        ends_at: endsAt.toISOString(),
        starts_at: startsAt?.toISOString() ?? null,
        max_per_user: maxPerUser,
        badge: form.getFieldValue("offer_badge") as OfferBadge | null,
        priority: (form.getFieldValue("offer_priority") as number | null) ?? 0,
      },
      { onSuccess: () => void message.success(t("robots.offerResetDone")) },
    );
  };

  if (!isNew && existing.isPending) return <Skeleton active />;
  if (!isNew && existing.isError) {
    return <Alert type="error" showIcon title={errorMessage(existing.error)} />;
  }

  const stored = existing.data;

  // Начальные значения собираются из полей, которые форма правит, а не из
  // ответа целиком. Иначе в тело PATCH уезжали бы `id`, `created_at`,
  // `image_url` и все автометрики — сейчас их молча отбрасывает бэкенд, но
  // отправлять оператору доступные только на чтение цифры обратно нельзя.
  const initial: Values = isNew
    ? DEFAULTS
    : {
        code: stored?.code ?? "",
        name: stored?.name ?? "",
        description: stored?.description ?? "",
        how_it_works: stored?.how_it_works ?? "",
        market: stored?.market ?? "spot",
        risk_level: stored?.risk_level ?? "medium",
        risk_note: stored?.risk_note ?? null,
        status: stored?.status ?? "active",
        sort_order: stored?.sort_order ?? 0,
        min_amount: stored?.min_amount ?? "0",
        recommended_amount: stored?.recommended_amount ?? null,
        min_topup_amount: stored?.min_topup_amount ?? "0",
        topup_enabled: stored?.topup_enabled ?? true,
        demo_enabled: stored?.demo_enabled ?? true,
        yield_type: stored?.yield_type ?? "target",
        declared_yield_12m: stored?.declared_yield_12m ?? "0",
        declared_max_dd: stored?.declared_max_dd ?? "0",
        daily_min_pct: stored?.daily_min_pct ?? "0",
        daily_max_pct: stored?.daily_max_pct ?? "0",
        positive_chance: stored?.positive_chance ?? "0",
        profit_fee_pct: stored?.profit_fee_pct ?? "0",
        users_count: stored?.users_count ?? 0,
        profit_total: stored?.profit_total ?? "0",
        assets: stored?.assets ?? [],
        ai_signals: stored?.ai_signals ?? [],
        tier_includes: stored?.tier_includes ?? [],
        exchanges: stored?.exchanges ?? [],
        pairs: stored?.pairs ?? [],
        accent_color: stored?.accent_color ?? null,
        tagline: stored?.tagline ?? null,
        ladder_note: stored?.ladder_note ?? null,
        about_strategy: stored?.about_strategy ?? null,
        disclaimer: stored?.disclaimer ?? null,
        cta_text: stored?.cta_text ?? null,
        leverage: stored?.leverage ?? null,
        timeframe: stored?.timeframe ?? null,
        trades_per_day: stored?.trades_per_day ?? null,
        avg_hold: stored?.avg_hold ?? null,
        trades_per_day_min: stored?.trades_per_day_min ?? null,
        trades_per_day_max: stored?.trades_per_day_max ?? null,
        visible_blocks_ui: blocksToForm(stored?.visible_blocks),
        is_limited: stored?.is_limited ?? false,
        pool_limit: stored?.pool_limit ?? null,
        max_per_user: stored?.max_per_user ?? null,
        offer_badge: stored?.offer_badge ?? "limited",
        offer_priority: stored?.offer_priority ?? 0,
        offer_paused: stored?.offer_paused ?? false,
        offer_starts_at_ui: toOfferTime(stored?.offer_starts_at),
        offer_ends_at_ui: toOfferTime(stored?.offer_ends_at),
        launched_at_ui: toOfferTime(stored?.launched_at),
      };

  const currentImage = pendingImage?.preview ?? stored?.image_url ?? undefined;

  // Рекомендованная сумма, а не минимум: превью должно показывать доходность на
  // типичном капитале, а не на пороговом. Оператор может поправить руками.
  const effectiveBase =
    previewBase ?? (recommended as string | null) ?? (minAmount) ?? "0";

  const previewRobot: PreviewRobot = {
    name: (watchedName) ?? "",
    code: (watchedCode) ?? "",
    image_url: currentImage ?? null,
    accent_color: (watchedAccent) ?? null,
    market: (market) ?? "spot",
    risk_level: (watchedRisk) ?? "medium",
    min_amount: (minAmount) ?? "0",
    declared_yield_12m: (declared) ?? "0",
    // Скрытые монеты не показываются, порядок — из списка: то же, что делает
    // `visible_assets()` на бэкенде.
    assets: ((watchedAssets as AssetItem[] | undefined) ?? [])
      .filter((item) => item.visible !== false)
      .map((item) => item.symbol)
      .filter(Boolean),
    ai_signals: (watchedSignals) ?? [],
    users_count: (watchedUsers) ?? 0,
    profit_fee_pct: (fee) ?? "0",
    tagline: (watchedTagline) ?? null,
    cta_text: (watchedCta) ?? null,
  };

  const choose = (file: File) => {
    // Новый робот: файл ждёт создания — id для пути ручки ещё нет.
    if (isNew) {
      setPendingImage((previous) => {
        if (previous) URL.revokeObjectURL(previous.preview);
        return { file, preview: URL.createObjectURL(file) };
      });
      return;
    }
    upload.mutate(
      { id: robotId, file },
      {
        onSuccess: () => {
          void message.success(t("robots.imageUploaded"));
        },
      },
    );
  };

  const removeImage = () => {
    if (isNew) {
      setPendingImage((previous) => {
        if (previous) URL.revokeObjectURL(previous.preview);
        return null;
      });
      return;
    }
    // Снятие картинки — обычный PATCH: `image_key: null`. Загрузка идёт
    // отдельной ручкой, а очистка — обычное поле, и незачем заводить под неё
    // третий эндпоинт.
    update.mutate({ image_key: null }, {
      onSuccess: () => {
        void message.success(t("robots.imageRemoved"));
      },
    });
  };

  const submit = ({
    visible_blocks_ui,
    offer_starts_at_ui,
    offer_ends_at_ui,
    launched_at_ui,
    ...rest
  }: Values) => {
    const body: RobotCreateBody = {
      ...rest,
      visible_blocks: blocksToBody(visible_blocks_ui, stored?.visible_blocks),
      // В UTC, а не в локальной зоне: `TIMESTAMPTZ` на бэкенде сравнивается
      // абсолютно, и смещение из строки он всё равно нормализует — но
      // отправлять «то, что видел оператор» без зоны означало бы сдвиг.
      offer_starts_at: offer_starts_at_ui?.toISOString() ?? null,
      offer_ends_at: offer_ends_at_ui?.toISOString() ?? null,
      launched_at: launched_at_ui?.toISOString() ?? null,
    };

    if (isNew) {
      create.mutate(body, {
        onSuccess: (created) => {
          void message.success(t("robots.created"));
          if (pendingImage) {
            upload.mutate(
              { id: created.id, file: pendingImage.file },
              { onSuccess: () => void navigate(ROUTES.robots) },
            );
            return;
          }
          void navigate(ROUTES.robots);
        },
      });
      return;
    }
    // `code` не меняется — он внешний идентификатор карточки и уже разошёлся
    // по ссылкам и логам. Бэкенд его в PATCH и не принимает.
    const patch: Record<string, unknown> = { ...body };
    delete patch.code;
    update.mutate(patch, {
      onSuccess: () => {
        void message.success(t("robots.updated"));
      },
    });
  };

  const saveButton = (
    <Button
      type="primary"
      htmlType="submit"
      form="robot-form"
      icon={<SaveOutlined aria-hidden />}
      loading={create.isPending || update.isPending}
    >
      {t("common.save")}
    </Button>
  );

  const cardTitle = (icon: React.ReactNode, title: string) => ({ icon, title });

  return (
    <>
      <PageHeader
        section="robots"
        title={isNew ? t("robots.createTitle") : t("robots.editTitle", { name: initial.name })}
        subtitle={t("robots.subtitle")}
        before={
          <Button
            icon={<ArrowLeftOutlined aria-hidden />}
            shape="circle"
            aria-label={t("common.back")}
            onClick={() => {
              void navigate(ROUTES.robots);
            }}
          />
        }
        // Дубль кнопки в шапке: форма стала длинной, и единственная кнопка внизу
        // означала бы скролл на всю страницу ради сохранения. Нативный
        // `form="id"` работает и вне тега <form>.
        //
        // Ссылка на аналитику — только у сохранённого робота: у ещё не
        // созданного нет ни id, ни покупок, ни просмотров.
        extra={
          <>
            {!isNew && (
              <Link to={ROUTES.robotAnalytics(robotId)}>
                <Button icon={<LineChartOutlined aria-hidden />}>
                  {t("robots.analytics")}
                </Button>
              </Link>
            )}
            {saveButton}
          </>
        }
      />

      <Form<Values>
        id="robot-form"
        form={form}
        layout="vertical"
        initialValues={initial}
        onFinish={submit}
        scrollToFirstError
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={13}>
            <Space orientation="vertical" size={16} style={{ display: "flex" }}>
              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<RobotOutlined aria-hidden />, t("robots.card"))}
              >
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="code"
                      label={t("robots.code")}
                      extra={t("robots.codeHint")}
                      rules={[
                        { required: true, message: t("login.required") },
                        { pattern: /^[a-z0-9_-]{2,32}$/, message: t("robots.codeHint") },
                      ]}
                    >
                      <Input disabled={!isNew} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label={t("robots.name")}
                      rules={[{ required: true, message: t("login.required") }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="description"
                  label={t("robots.description")}
                  rules={[{ required: true, message: t("login.required") }]}
                >
                  <Input.TextArea rows={3} />
                </Form.Item>

                <Form.Item
                  name="how_it_works"
                  label={t("robots.howItWorks")}
                  rules={[{ required: true, message: t("login.required") }]}
                >
                  <Input.TextArea rows={4} />
                </Form.Item>

                <Row gutter={12}>
                  <Col span={6}>
                    <Form.Item name="market" label={t("robots.market")}>
                      <Select
                        options={[
                          { value: "spot", label: t("robots.market_spot") },
                          { value: "futures", label: t("robots.market_futures") },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="risk_level" label={t("robots.riskLevel")}>
                      <Select
                        options={[
                          { value: "low", label: t("robots.risk_low") },
                          { value: "medium", label: t("robots.risk_medium") },
                          { value: "high", label: t("robots.risk_high") },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    {/* §4.1 доработок: «LOW / MEDIUM / HIGH + краткое
                        пояснение». Пусто — витрина берёт дефолтную
                        формулировку по уровню, поэтому поле необязательное:
                        три буквы без расшифровки не должны доехать до
                        карточки ни у одного робота. */}
                    <Form.Item
                      name="risk_note"
                      label={t("robots.riskNote")}
                      extra={t("robots.riskNoteHint")}
                      rules={[{ max: 120, message: t("robots.riskNoteTooLong") }]}
                      normalize={(value: string) => (value.trim() === "" ? null : value)}
                    >
                      <Input maxLength={120} showCount />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    {/* Четыре состояния (§30), а не переключатель: «остановлен»
                        и «скрыт» — разные вещи, и остановка обязана сохранять
                        историю, а не убирать робота из каталога. */}
                    <Form.Item
                      name="status"
                      label={t("robots.status")}
                      extra={t("robots.statusHint")}
                    >
                      <Select
                        options={[
                          { value: "active", label: t("status.active") },
                          { value: "paused", label: t("status.paused") },
                          { value: "stopped", label: t("status.stopped") },
                          { value: "hidden", label: t("status.hidden") },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="sort_order" label={t("robots.sortOrder")}>
                      <InputNumber style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<WalletOutlined aria-hidden />, t("robots.money"))}
              >
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="min_amount"
                      label={t("robots.minAmount")}
                      extra={t("robots.minAmountHint")}
                      rules={[{ required: true, message: t("login.required") }]}
                    >
                      <InputNumber stringMode min="0" step="10" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="recommended_amount"
                      label={t("robots.recommendedAmount")}
                      extra={t("robots.recommendedHint")}
                    >
                      <InputNumber stringMode min="0" step="10" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="min_topup_amount" label={t("robots.minTopupAmount")}>
                      <InputNumber stringMode min="0" step="10" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="topup_enabled"
                      label={t("robots.topupEnabled")}
                      extra={t("robots.topupHint")}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  {/* Доступность в демо — свойство робота, а не список
                      идентификаторов в настройках: правится там же, где
                      остальная карточка, и не протухает, когда робота
                      снимают с витрины (доработки §7). */}
                  <Col span={12}>
                    <Form.Item
                      name="demo_enabled"
                      label={t("robots.demoEnabled")}
                      extra={t("robots.demoEnabledHint")}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="profit_fee_pct"
                  label={t("robots.profitFeePct")}
                  extra={t("robots.profitFeeHint")}
                >
                  {/* Потолок 15 держит и схема бэкенда — здесь он стоит,
                      чтобы оператор упёрся в него на форме, а не в 422. */}
                  <InputNumber stringMode min="0" max="15" step="1" style={{ width: "50%" }} />
                </Form.Item>
              </SectionCard>

              {/* Лимитированное предложение (доработки §2). Сразу после «Денег»:
                  речь про суммы и пороги, и оператор ищет их рядом. */}
              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<ThunderboltOutlined aria-hidden />, t("robots.offer"))}
              >
                <Form.Item
                  name="is_limited"
                  label={t("robots.isLimited")}
                  extra={t("robots.isLimitedHint")}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>

                {/* Поля не размонтируются, а гаснут: оператор должен видеть,
                    что уедет в тело сохранения, когда он вернёт переключатель. */}
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="pool_limit"
                      label={t("robots.poolLimit")}
                      extra={t("robots.poolLimitHint")}
                      rules={[{ required: isLimited, message: t("login.required") }]}
                    >
                      <InputNumber
                        stringMode
                        min="0"
                        step="1000"
                        disabled={!isLimited}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="max_per_user"
                      label={t("robots.maxPerUser")}
                      extra={t("robots.maxPerUserHint")}
                    >
                      <InputNumber
                        stringMode
                        min="0"
                        step="100"
                        disabled={!isLimited}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="offer_starts_at_ui"
                      label={t("robots.offerStartsAt")}
                      extra={t("robots.offerStartsHint")}
                    >
                      <DatePicker
                        showTime
                        format="DD.MM.YYYY HH:mm"
                        disabled={!isLimited}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="offer_ends_at_ui"
                      label={t("robots.offerEndsAt")}
                      dependencies={["offer_starts_at_ui"]}
                      rules={[
                        { required: isLimited, message: t("login.required") },
                        // Порядок окна держит и бэкенд, и CHECK в базе. Здесь он
                        // стоит, чтобы оператор упёрся в него на поле, а не в
                        // 422 после сорока полей.
                        ({ getFieldValue }) => ({
                          validator(_rule, value: Dayjs | null) {
                            const startsAt = getFieldValue("offer_starts_at_ui") as
                              | Dayjs
                              | null;
                            if (!value || !startsAt || startsAt.isBefore(value)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t("robots.offerWindowOrder")));
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        showTime
                        format="DD.MM.YYYY HH:mm"
                        disabled={!isLimited}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Абсолютный момент со смещением. Оператор думает в московском
                    времени, а `DatePicker` работает в зоне браузера: без этой
                    строки ноутбук на UTC поставил бы дедлайн на три часа мимо, и
                    обнаружилось бы это ровно в момент дедлайна. */}
                {isLimited && endsAtUi ? (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t("robots.offerEndsAbsolute", {
                      moment: endsAtUi.format("DD.MM.YYYY HH:mm"),
                      offset: endsAtUi.format("Z"),
                    })}
                  />
                ) : null}

                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item name="offer_badge" label={t("robots.offerBadge")}>
                      <Select
                        disabled={!isLimited}
                        options={OFFER_BADGES.map((code) => ({
                          value: code,
                          label: t(`robots.offerBadgeValue.${code}`),
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="offer_priority"
                      label={t("robots.offerPriority")}
                      extra={t("robots.offerPriorityHint")}
                    >
                      <InputNumber min={0} max={1000} disabled={!isLimited} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="offer_paused"
                      label={t("robots.offerPaused")}
                      extra={t("robots.offerPausedHint")}
                      valuePropName="checked"
                    >
                      <Switch disabled={!isLimited} />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Счётчик и всё, что из него выводится, — вне `Form`: цифра,
                    которую нельзя вводить, обязана быть отличима от той, которую
                    можно, иначе оператор однажды перезапишет вторую первой. */}
                {!isNew && stored?.is_limited ? (
                  <div>
                    <Divider titlePlacement="start" style={{ marginTop: 0 }}>
                      {t("robots.offerProgress")}
                    </Divider>
                    <Progress percent={Number(stored.pool_fill_pct)} status="active" />
                    <Row gutter={12} style={{ marginTop: 12 }}>
                      <Col span={8}>
                        <Statistic
                          title={t("robots.poolUsed")}
                          value={stored.pool_used}
                          suffix="USDT"
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title={t("robots.poolAvailable")}
                          value={stored.pool_available}
                          suffix="USDT"
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title={t("robots.offerState")}
                          valueRender={() => (
                            <Tag>{t(`robots.offerStateValue.${stored.offer_state ?? "active"}`)}</Tag>
                          )}
                        />
                      </Col>
                    </Row>
                    <Popconfirm
                      title={t("robots.offerReset")}
                      description={t("robots.offerResetConfirm")}
                      okText={t("common.save")}
                      cancelText={t("common.cancel")}
                      onConfirm={resetOffer}
                    >
                      <Button danger style={{ marginTop: 12 }} loading={reset.isPending}>
                        {t("robots.offerReset")}
                      </Button>
                    </Popconfirm>
                    <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                      {t("robots.offerResetHint")}
                    </Typography.Paragraph>
                  </div>
                ) : null}
              </SectionCard>

              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<EditOutlined aria-hidden />, t("robots.manualData"))}
                extra={
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("robots.manualDataHint")}
                  </Typography.Text>
                }
              >
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="declared_yield_12m"
                      label={t("robots.declaredYield")}
                      extra={t("robots.declaredYieldHint")}
                      rules={[{ required: true, message: t("login.required") }]}
                    >
                      <InputNumber stringMode step="0.1" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="declared_max_dd"
                      label={t("robots.declaredMaxDd")}
                      rules={[{ required: true, message: t("login.required") }]}
                    >
                      <InputNumber stringMode min="0" step="0.1" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    {/* Чем витрина объявит показанную доходность (§2.1, §4.1).
                        «Целевая» по умолчанию: ряд, который рисует график и
                        лесенку процентов, генерируется из настроек робота, то
                        есть по построению является моделью. Назвать его
                        фактической историей — сознательное утверждение
                        оператора, а не молчаливый дефолт. */}
                    <Form.Item
                      name="yield_type"
                      label={t("robots.yieldType")}
                      extra={t("robots.yieldTypeHint")}
                    >
                      <Select
                        options={[
                          { value: "target", label: t("robots.yieldType_target") },
                          { value: "historical", label: t("robots.yieldType_historical") },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    {/* «Период работы» §4.1. Пусто — витрина считает от даты
                        заведения карточки: дублировать её руками оператор не
                        обязан. Заполняется, когда стратегия работала до
                        появления в этом продукте. */}
                    <Form.Item
                      name="launched_at_ui"
                      label={t("robots.launchedAt")}
                      extra={t("robots.launchedAtHint")}
                    >
                      <DatePicker showTime style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    {/* Под ручной цифрой — её автоматический близнец. Одних
                        разных карточек мало: понять, что `users_count` витринный,
                        а посчитанный настоящий, оператор должен там, где
                        печатает, а не двумя экранами ниже.

                        Близнец здесь именно `active_users`, а не
                        `participants`: витрина подписывает эту строку
                        «Используют сейчас», и сверять ручную цифру нужно с
                        тем, что заменит её на экране, а не со всеми покупками
                        за историю — остановленные в счётчик не входят. */}
                    <Form.Item
                      name="users_count"
                      label={t("robots.usersCount")}
                      extra={
                        isNew
                          ? t("robots.showcaseHint")
                          : t("robots.realNow", { value: stored?.active_users ?? 0 })
                      }
                    >
                      <InputNumber min={0} step={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="profit_total"
                      label={t("robots.profitTotal")}
                      extra={
                        isNew
                          ? undefined
                          : t("robots.realNow", { value: stored?.user_profit_total ?? "0" })
                      }
                    >
                      <InputNumber stringMode min="0" step="100" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              {/* Автоматические данные (§25). Рисуются мимо `Form` — не из
                  вежливости, а чтобы физически не попасть в тело сохранения.
                  У нового робота карточки нет: четыре нуля новичку — шум,
                  который потом перестают читать. */}
              {!isNew && (
                <SectionCard
                  color={SECTIONS.robots.color}
                  {...cardTitle(<BarChartOutlined aria-hidden />, t("robots.autoData"))}
                  extra={<Tag>{t("robots.readOnly")}</Tag>}
                >
                  <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                    {t("robots.autoDataHint")}
                  </Typography.Paragraph>
                  <Row gutter={12}>
                    <Col span={5}>
                      <Statistic
                        title={t("robots.activeUsers")}
                        value={stored?.active_users ?? 0}
                      />
                    </Col>
                    <Col span={5}>
                      <Statistic
                        title={t("robots.participants")}
                        value={stored?.participants ?? 0}
                      />
                    </Col>
                    <Col span={4}>
                      <Statistic title={t("robots.purchasesCount")} value={stored?.purchases_count ?? 0} />
                    </Col>
                    <Col span={5}>
                      <Statistic title={t("robots.aumLabel")} value={stored?.aum ?? "0"} suffix="USDT" />
                    </Col>
                    <Col span={5}>
                      <Statistic
                        title={t("robots.userProfitTotal")}
                        value={stored?.user_profit_total ?? "0"}
                        suffix="USDT"
                      />
                    </Col>
                  </Row>
                </SectionCard>
              )}

              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<SlidersOutlined aria-hidden />, t("robots.generator"))}
              >
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item
                      name="daily_min_pct"
                      label={t("robots.dailyMin")}
                      rules={[
                        { required: true, message: t("login.required") },
                        // Ноль обязан входить в диапазон — то же требование, что
                        // в генераторе. Проверка здесь избавляет от 422 после
                        // отправки формы на сорок полей.
                        {
                          validator: (_, value: string) =>
                            Number(value) <= 0
                              ? Promise.resolve()
                              : Promise.reject(new Error(t("robots.rangeError"))),
                        },
                      ]}
                    >
                      <InputNumber stringMode max="0" step="0.1" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="daily_max_pct"
                      label={t("robots.dailyMax")}
                      rules={[
                        { required: true, message: t("login.required") },
                        {
                          validator: (_, value: string) =>
                            Number(value) >= 0
                              ? Promise.resolve()
                              : Promise.reject(new Error(t("robots.rangeError"))),
                        },
                      ]}
                    >
                      <InputNumber stringMode min="0" step="0.1" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="positive_chance"
                      label={t("robots.positiveChance")}
                      rules={[{ required: true, message: t("login.required") }]}
                    >
                      <InputNumber stringMode min="0" max="100" step="1" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<PictureOutlined aria-hidden />, t("robots.appearance"))}
              >
                <Row gutter={12}>
                  <Col span={14}>
                    <Form.Item label={t("robots.image")} extra={t("robots.imageHint")}>
                      <Space align="center" size={12}>
                        <Avatar
                          size={56}
                          shape="square"
                          src={currentImage}
                          icon={<RobotOutlined aria-hidden />}
                          style={{ borderRadius: 16, flex: "none" }}
                        />
                        <Space orientation="vertical" size={4}>
                          <Upload
                            accept="image/png,image/jpeg,image/webp"
                            showUploadList={false}
                            // antd иначе пойдёт слать файл сам, своим запросом
                            // мимо клиента и мимо токена админки.
                            beforeUpload={(file) => {
                              choose(file);
                              return false;
                            }}
                          >
                            <Button icon={<UploadOutlined aria-hidden />} loading={upload.isPending}>
                              {t("robots.imageChoose")}
                            </Button>
                          </Upload>
                          {currentImage ? (
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined aria-hidden />}
                              onClick={removeImage}
                            >
                              {t("robots.imageRemove")}
                            </Button>
                          ) : null}
                          {isNew && pendingImage ? (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {t("robots.imagePendingHint")}
                            </Typography.Text>
                          ) : null}
                        </Space>
                      </Space>
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      name="accent_color"
                      label={t("robots.accentColor")}
                      extra={t("robots.accentColorHint")}
                      // Форма хранит строку «#rrggbb» — ровно то, что принимает
                      // контракт. Объект Color из antd уехал бы в тело запроса
                      // как `{}` и упал бы на 422.
                      getValueFromEvent={(color: { toHexString: () => string } | null) =>
                        color ? color.toHexString() : null
                      }
                    >
                      <ColorPicker format="hex" disabledAlpha allowClear showText />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<ShopOutlined aria-hidden />, t("robots.showcase"))}
              >
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="tagline" label={t("robots.tagline")} extra={t("robots.taglineHint")}>
                      <Input maxLength={120} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="ladder_note" label={t("robots.ladderNote")} extra={t("robots.ladderHint")}>
                      <Input maxLength={120} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="about_strategy" label={t("robots.aboutStrategy")} extra={t("robots.aboutHint")}>
                  <Input.TextArea rows={3} maxLength={400} showCount />
                </Form.Item>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="cta_text" label={t("robots.ctaText")} extra={t("robots.ctaHint")}>
                      <Input maxLength={60} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="tier_includes"
                      label={t("robots.tierIncludes")}
                      extra={t("robots.tierIncludesHint")}
                    >
                      <Select mode="tags" open={false} suffixIcon={null} tokenSeparators={[";"]} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="disclaimer"
                  label={t("robots.disclaimer")}
                  extra={t("robots.disclaimerHint")}
                >
                  <Input.TextArea rows={2} maxLength={500} showCount />
                </Form.Item>

                {/* §26. Список отмеченных, а не словарь переключателей: пустая
                    запись означает «показывать всё», поэтому в тело имеет смысл
                    писать только выключенное. */}
                <Form.Item
                  name="visible_blocks_ui"
                  label={t("robots.visibleBlocks")}
                  extra={t("robots.visibleBlocksHint")}
                >
                  <Checkbox.Group
                    options={SHOWCASE_BLOCKS.map((code) => ({
                      value: code,
                      label: t(`robots.block_${code}`),
                    }))}
                  />
                </Form.Item>
              </SectionCard>

              <SectionCard
                color={SECTIONS.robots.color}
                {...cardTitle(<LineChartOutlined aria-hidden />, t("robots.trading"))}
              >
                <Form.Item label={t("robots.assets")} extra={t("robots.assetsHint")}>
                  <Form.List
                    name="assets"
                    rules={[
                      {
                        // Правило уровня списка, а не поля: антд ревалидирует
                        // только тронутое поле, и правка третьей строки не гасила
                        // бы ошибку на первой.
                        validator: (_, items: AssetItem[] | undefined) => {
                          const symbols = (items ?? [])
                            .map((item) => item?.symbol)
                            .filter(Boolean);
                          return new Set(symbols).size === symbols.length
                            ? Promise.resolve()
                            : Promise.reject(new Error(t("robots.assetsDuplicate")));
                        },
                      },
                    ]}
                  >
                    {(fields, { add, remove, move }, { errors }) => (
                      <>
                        {fields.map((field, index) => (
                          <Row
                            key={field.key}
                            gutter={8}
                            align="middle"
                            style={{ marginBottom: 8, flexWrap: "nowrap" }}
                          >
                            <Col flex="auto" style={{ minWidth: 0 }}>
                              <Form.Item
                                name={[field.name, "symbol"]}
                                style={{ marginBottom: 0 }}
                                // Верхний регистр здесь, а не на сервере: иначе
                                // «btc» и «BTC» проходят проверку на дубли и
                                // падают на 422.
                                normalize={(value: string) => value.toUpperCase().trim()}
                                rules={[
                                  { required: true, message: t("login.required") },
                                  { pattern: /^[A-Z0-9]{2,10}$/, message: t("robots.assetsHint") },
                                ]}
                              >
                                <Input placeholder="BTC" />
                              </Form.Item>
                            </Col>
                            <Col flex="0 0 auto">
                              <Form.Item
                                name={[field.name, "visible"]}
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                              >
                                <Switch
                                  aria-label={t("robots.assetVisible")}
                                  checkedChildren={<EyeOutlined aria-hidden />}
                                  unCheckedChildren={<EyeInvisibleOutlined aria-hidden />}
                                />
                              </Form.Item>
                            </Col>
                            <Col flex="0 0 auto">
                              <Button
                                type="text"
                                shape="circle"
                                aria-label={t("robots.assetUp")}
                                icon={<ArrowUpOutlined aria-hidden />}
                                disabled={index === 0}
                                onClick={() => {
                                  move(index, index - 1);
                                }}
                              />
                            </Col>
                            <Col flex="0 0 auto">
                              <Button
                                type="text"
                                shape="circle"
                                aria-label={t("robots.assetDown")}
                                icon={<ArrowDownOutlined aria-hidden />}
                                disabled={index === fields.length - 1}
                                onClick={() => {
                                  move(index, index + 1);
                                }}
                              />
                            </Col>
                            <Col flex="0 0 auto">
                              <Button
                                danger
                                type="text"
                                shape="circle"
                                aria-label={t("robots.assetRemove")}
                                icon={<DeleteOutlined aria-hidden />}
                                onClick={() => {
                                  remove(field.name);
                                }}
                              />
                            </Col>
                          </Row>
                        ))}
                        <Form.ErrorList errors={errors} />
                        <Button
                          type="dashed"
                          block
                          icon={<PlusOutlined aria-hidden />}
                          // Потолок продублирован кнопкой: упереться в
                          // неактивную понятнее, чем получить сообщение после
                          // заполнения строки.
                          disabled={fields.length >= MAX_ASSETS}
                          onClick={() => {
                            // Явный `visible: true`: `add()` создаёт строку без
                            // поля, и переключатель нарисовался бы выключенным у
                            // монеты, которую сервер считает видимой.
                            add({ symbol: "", visible: true });
                          }}
                        >
                          {t("robots.assetAdd")}
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Form.Item>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="exchanges" label={t("robots.exchanges")} extra={t("robots.tagsHint")}>
                      <Select mode="tags" open={false} suffixIcon={null} tokenSeparators={[","]} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="pairs" label={t("robots.pairs")} extra={t("robots.pairsHint")}>
                      <Select mode="tags" open={false} suffixIcon={null} tokenSeparators={[",", " "]} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="ai_signals"
                  label={t("robots.aiSignals")}
                  extra={t("robots.aiSignalsHint")}
                  rules={[
                    {
                      // Тот же запрет, что в `validate_signals` на бэкенде: у
                      // спотовой пары нет ни открытого интереса, ни ставки
                      // финансирования, ни ликвидаций.
                      validator: (_, value: string[] | undefined) => {
                        const bad =
                          market === "spot" &&
                          (value ?? []).some((code) => FUTURES_ONLY.has(code));
                        return bad
                          ? Promise.reject(new Error(t("robots.aiSignalsSpot")))
                          : Promise.resolve();
                      },
                    },
                  ]}
                >
                  {/* Набор фиксирован контрактом, поэтому `multiple` со
                      списком, а не `tags`: опечатка в коде сигнала обернулась
                      бы 422 на сохранении, а не подсказкой на форме. */}
                  <Select mode="multiple" options={AI_SIGNAL_OPTIONS} allowClear />
                </Form.Item>

                <Row gutter={12}>
                  <Col span={6}>
                    <Form.Item name="leverage" label={t("robots.leverage")}>
                      <Input maxLength={64} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="timeframe" label={t("robots.timeframe")}>
                      <Input maxLength={64} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="trades_per_day" label={t("robots.tradesPerDay")}>
                      <Input maxLength={64} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="avg_hold" label={t("robots.avgHold")}>
                      <Input maxLength={64} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="trades_per_day_min"
                      label={t("robots.tradesMin")}
                      extra={t("robots.tradesBandHint")}
                    >
                      <InputNumber min={1} max={500} step={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="trades_per_day_max" label={t("robots.tradesMax")}>
                      <InputNumber min={1} max={500} step={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <Space>
                {saveButton}
                <Button
                  icon={<CloseOutlined aria-hidden />}
                  onClick={() => {
                    void navigate(ROUTES.robots);
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </Space>
            </Space>
          </Col>

          <Col xs={24} lg={11}>
            {/* Обёртка обязательна: `SectionCard` задаёт `height: 100%`, а `Col`
                во flex-строке растянут по высоте левой колонки — без неё
                карточка превью растянулась бы на всю форму. */}
            <div style={{ marginBottom: 16 }}>
              <YieldPreviewCard
                params={{
                  base: String(effectiveBase ?? ""),
                  daily_min_pct: String(dailyMin ?? ""),
                  daily_max_pct: String(dailyMax ?? ""),
                  positive_chance: String(chance ?? ""),
                  profit_fee_pct: String(fee ?? "0"),
                }}
                declaredYield={String(declared ?? "0")}
                base={String(effectiveBase ?? "")}
                onBaseChange={setPreviewBase}
              />
            </div>

            {/* Липнет к шапке: пока оператор правит витрину и торговлю,
                превью карточки висит ровно над теми полями, которые рисует.
                `sticky`, а не `Affix`: последний вынимает элемент из потока,
                и соседняя карточка прыгает. */}
            <div style={{ position: "sticky", top: 68 }}>
              <RobotCardPreview robot={previewRobot} />
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
}
